import { Icon } from '@iconify/react';
// mdi:chart-bell-curve
import chartBellCurve from '@iconify/icons-mdi/chart-bell-curve';
// mdi:table-of-contents
import tableOfContents from '@iconify/icons-mdi/table-of-contents';

import PropTypes from 'prop-types';
import { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';

import moment from 'moment';

import { styled } from '@mui/material/styles';
import {
  Grid,
  Card,
  Stack,
  useMediaQuery,
  Typography,
  Paper,
  BottomNavigationAction,
  Box,
  BottomNavigation,
  Divider,
  Skeleton,
  Button
} from '@mui/material';

import { MHidden } from '../components/@material-extend';
import Page from '../components/Page';
import Scrollbar from '../components/Scrollbar';
import {
  FlightReviewSideBar,
  FlightReviewGraph,
  FlightReviewGraphLanding,
  FlightReviewIndicators,
  FlightReviewSideBarHeader
} from '../components/flightReview';
import { useEffectAsync } from '../reactHelper';
import { getReports, getReportsPositions } from '../fetch';
import { ReplayPathMap3d, GeofenceMap, Add3DElement, Map, AddSatelliteView } from '../map';
import { useTranslation } from '../common/LocalizationProvider';
import { selectDevicesArray, selectReportTripsArray } from '../createSelectorUtils';

// ----------------------------------------------------------------------
function TabPanel(props) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`vertical-tabpanel-${index}`}
      aria-labelledby={`vertical-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 0 }}>{children}</Box>}
    </div>
  );
}

TabPanel.propTypes = {
  children: PropTypes.node,
  index: PropTypes.number.isRequired,
  value: PropTypes.number.isRequired
};

// ----------------------------------------------------------------------
const RootStyle = styled('div')(() => ({
  flexGrow: 1
}));

const MapStyle = styled(Card)(({ theme }) => ({
  height: window.innerHeight / 2,
  width: '100%',
  position: 'relative',
  [theme.breakpoints.between('xs', 'md')]: {
    width: window.innerWidth,
    height: window.innerHeight / 1.3
  }
}));
const MapSkeleton = styled(Skeleton)(({ theme }) => ({
  height: window.innerHeight / 2,
  width: '100%',
  position: 'relative',
  [theme.breakpoints.between('xs', 'md')]: {
    width: window.innerWidth
  }
}));

const SideBar = styled(Paper)(({ theme }) => ({
  width: '100%',
  position: 'relative',
  height: window.innerHeight / 2,
  [theme.breakpoints.between('xs', 'md')]: {
    width: window.innerWidth
  }
}));

export default function FlightReview() {
  const t = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const Up = useMediaQuery((theme) => theme.breakpoints.up('lg'));
  const isDown = useMediaQuery((theme) => theme.breakpoints.down('sm'));

  const [reportTrip, setReportTrip] = useState(
    useSelector(selectReportTripsArray).find(
      (item) =>
        item.startTime === location.state.startTime &&
        item.endTime === location.state.endTime &&
        item.deviceId === location.state.deviceId
    ) || {}
  );

  const [analyse, setAnalyse] = useState({
    analyseAtterrissageReports: [],
    analyseDecollageReports: [],
    analyseRoulageReports: [],
    analyseVolReports: []
  });
  const [positions, setPositions] = useState([]);
  const [events, setEvents] = useState([]);

  const [valueTabs, setValueTabs] = useState(0);
  const handleChangeTabs = (event, newValue) => {
    setValueTabs(newValue);
  };

  const [indexDisplay, setIndexDisplay] = useState(0);
  const [state, setState] = useState({
    currentPosition: 0,
    currentPhase: 'all',
    playVol: false,
    speedReplay: 1,
    selectedListItem: -1,
    refreshAnalyse: false,
    refresh: false
  });

  const device =
    useSelector(selectDevicesArray).find((item) => item.id === location.state.deviceId) || {};

  useEffectAsync(async () => {
    if (location.state !== null) {
      const response = await getReports(
        'analyse',
        location.state.deviceId,
        0,
        0,
        moment(location.state.startTime).toISOString(),
        moment(location.state.endTime).toISOString(),
        false
      );
      if (response.ok) {
        const result = await response.json();
        if (result.length > 0) {
          setAnalyse(result.shift());
        } else {
          setAnalyse({
            analyseAtterrissageReports: [],
            analyseDecollageReports: [],
            analyseRoulageReports: [],
            analyseVolReports: []
          });
        }
      }

      const responsePositions = await getReportsPositions(
        location.state.deviceId,
        moment(location.state.startTime).toISOString(),
        moment(location.state.endTime).toISOString()
      );
      if (responsePositions.ok) {
        setPositions(await responsePositions.json());
      }
    }
  }, [location.state, state.refreshAnalyse]);

  useEffectAsync(async () => {
    if (location.state !== null) {
      const responseEvents = await getReports(
        'events',
        location.state.deviceId,
        0,
        0,
        moment(location.state.startTime).toISOString(),
        moment(location.state.endTime).toISOString(),
        false
      );
      if (responseEvents.ok) {
        setEvents(await responseEvents.json());
      }
    }
  }, [location.state]);

  useEffectAsync(async () => {
    if (location.state !== null && (reportTrip == null || Object.keys(reportTrip).length === 0)) {
      const response = await getReports(
        'trips',
        location.state.deviceId,
        0,
        0,
        moment(location.state.startTime).toISOString(),
        moment(location.state.endTime).toISOString(),
        false
      );
      if (response.ok) {
        const result = await response.json();
        setReportTrip(
          result.find(
            (item) =>
              item.startTime === location.state.startTime &&
              item.endTime === location.state.endTime &&
              item.deviceId === location.state.deviceId
          ) || {}
        );
      }
    }
  }, [location.state]);

  return (
    <Page
      title={t('FR_Title')}
      openProgress={
        !(positions.length !== 0 && Object.keys(reportTrip).length !== 0) || state.refresh
      }
    >
      <RootStyle sx={Up ? { mt: -3, mb: -10, mr: 0, ml: 0 } : { mt: -3, mb: -10 }}>
        <Scrollbar
          sx={{
            height: '100%'
          }}
        >
          <MHidden width="mdUp">
            <Button
              onClick={() => navigate('/flight/list', { replace: true })}
              sx={{ mb: 1, ml: 1 }}
              variant="outlined"
              size="small"
            >
              <Icon icon="mdi:arrow-left-thin" width={24} height={24} />
              {t('btn_Back')}
            </Button>
          </MHidden>

          <Stack
            spacing={0}
            direction={{ xs: 'column', sm: 'row' }}
            alignItems="center"
            justifyContent="space-between"
          >
            <Grid container spacing={2}>
              <Grid item xs={12} sm={12} md={8} lg={8} xl={8}>
                <Stack spacing={0} direction="column">
                  <Stack spacing={0} direction="row">
                    {location.state.privateAccount ? (
                      <Stack spacing={0} direction="column" sx={{ width: '100%' }}>
                        <Typography variant="h5" color="error">
                          {t('FL_Private_Mode')}
                        </Typography>
                        <MapSkeleton variant="rectangular" />
                      </Stack>
                    ) : (
                      <MapStyle>
                        <Map>
                          <AddSatelliteView />
                          <Add3DElement />
                          <GeofenceMap />
                          {positions.length > 0 && (
                            <ReplayPathMap3d
                              positions={positions}
                              events={events}
                              analyse={analyse}
                              state={state}
                              setState={setState}
                              setIndexDisplay={setIndexDisplay}
                              isDown={isDown}
                            />
                          )}
                        </Map>
                      </MapStyle>
                    )}
                  </Stack>
                </Stack>
              </Grid>
              <MHidden width="mdDown">
                <Grid item xs={12} sm={12} md={4} lg={4} xl={4}>
                  <SideBar>
                    <FlightReviewSideBarHeader
                      reportTrip={reportTrip}
                      device={device}
                      state={state}
                      setState={setState}
                    />
                    <Divider />
                    {!state.playVol ? (
                      <FlightReviewSideBar
                        reportTrip={reportTrip}
                        analyse={analyse}
                        positions={positions}
                        events={events}
                        state={state}
                        setState={setState}
                        device={device}
                      />
                    ) : (
                      <FlightReviewIndicators positions={positions} indexDisplay={indexDisplay} />
                    )}
                  </SideBar>
                </Grid>
                <Grid item xs={12} sm={12} md={12} lg={12} xl={12} sx={{ mt: 2 }}>
                  {state.currentPhase === 'atterrissages' && state.selectedListItem !== -1 ? (
                    <FlightReviewGraphLanding
                      analyse={analyse}
                      positions={positions}
                      state={state}
                      setState={setState}
                    />
                  ) : (
                    <FlightReviewGraph
                      reportTrip={reportTrip}
                      analyse={analyse}
                      positions={positions}
                      events={events}
                      state={state}
                      setState={setState}
                      indexDisplay={indexDisplay}
                    />
                  )}
                </Grid>
              </MHidden>
              <MHidden width="mdUp">
                <Divider />
                <Grid item xs={12} sm={12} md={4} lg={4} xl={4} sx={{ mb: 10 }}>
                  <Box>
                    <BottomNavigation showLabels value={valueTabs} onChange={handleChangeTabs}>
                      <BottomNavigationAction
                        icon={<Icon icon={tableOfContents} width={24} height={24} />}
                      />
                      <BottomNavigationAction
                        icon={<Icon icon={chartBellCurve} width={24} height={24} />}
                      />
                    </BottomNavigation>
                  </Box>

                  <Divider />
                  <TabPanel value={valueTabs} index={0}>
                    <FlightReviewSideBarHeader
                      reportTrip={reportTrip}
                      device={device}
                      state={state}
                      setState={setState}
                    />
                    {!state.playVol ? (
                      <FlightReviewSideBar
                        reportTrip={reportTrip}
                        analyse={analyse}
                        positions={positions}
                        events={events}
                        state={state}
                        setState={setState}
                        device={device}
                      />
                    ) : (
                      <FlightReviewIndicators positions={positions} indexDisplay={indexDisplay} />
                    )}
                  </TabPanel>
                  <TabPanel value={valueTabs} index={1}>
                    {state.currentPhase === 'atterrissages' && state.selectedListItem !== -1 ? (
                      <FlightReviewGraphLanding
                        analyse={analyse}
                        positions={positions}
                        state={state}
                        setState={setState}
                      />
                    ) : (
                      <FlightReviewGraph
                        reportTrip={reportTrip}
                        analyse={analyse}
                        positions={positions}
                        events={events}
                        state={state}
                        setState={setState}
                        indexDisplay={indexDisplay}
                      />
                    )}
                  </TabPanel>
                </Grid>
              </MHidden>
            </Grid>
          </Stack>
        </Scrollbar>
      </RootStyle>
    </Page>
  );
}
